import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { PaymentIntentRefundDialog } from './PaymentIntentRefundDialog';

/**
 * PaymentIntentsSection — Admin table listing grouped payment intents
 * (multi-booking baskets) with per-row refund action. Lives inside the
 * Financial > Overview tab so admins can reconcile & refund without
 * leaving the P&L view.
 */
export function PaymentIntentsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('refundable'); // refundable | all
  const [openIntentId, setOpenIntentId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100 };
      if (statusFilter === 'refundable') params.status = 'paid,partially_refunded';
      else if (statusFilter === 'refunded') params.status = 'refunded';
      // 'all' passes no status → server default (paid + partially_refunded).
      // But we want ALL when user picks 'all', so send an inclusive set:
      if (statusFilter === 'all') params.status = 'paid,partially_refunded,refunded';
      if (search.trim()) params.search = search.trim();
      const data = await api.listPaymentIntents(params);
      setRows(data?.intents || []);
    } catch (err) {
      setError(err?.message || 'Failed to load payment intents');
      toast.error(err?.message || 'Failed to load payment intents');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const totalRefunded = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.refunded_amount || 0), 0),
    [rows]
  );

  const symbol = '£';

  return (
    <div className="card" style={{ marginTop: '1.5rem' }} data-testid="payment-intents-section">
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>🧾 Grouped Payments (Refundable Baskets)</h2>
          <p style={{
            margin: '0.15rem 0 0', fontSize: '0.8rem',
            color: 'hsl(var(--muted-foreground))',
          }}>
            Parents' multi-booking payments — refund whole basket or specific bookings.
            {rows.length > 0 && ` · ${rows.length} intents · ${symbol}${totalRefunded.toFixed(2)} refunded`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search parent name / email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            data-testid="payment-intents-search"
            style={{
              padding: '0.4rem 0.65rem', fontSize: '0.85rem',
              border: '1px solid hsl(var(--border))', borderRadius: 8,
              minWidth: 220,
            }}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="payment-intents-status-filter"
            style={{
              padding: '0.4rem 0.65rem', fontSize: '0.85rem',
              border: '1px solid hsl(var(--border))', borderRadius: 8,
              background: 'hsl(var(--background))',
            }}
          >
            <option value="refundable">Refundable (paid + partial)</option>
            <option value="refunded">Fully refunded</option>
            <option value="all">All statuses</option>
          </select>
          <button type="button" className="btn btn-ghost btn-sm"
                  onClick={load} data-testid="payment-intents-refresh"
                  disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{
          borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.85rem',
        }} data-testid="payment-intents-error">{error}</div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}
               data-testid="payment-intents-table">
          <thead>
            <tr style={{ textAlign: 'left', color: 'hsl(var(--muted-foreground))' }}>
              <Th>Parent</Th>
              <Th>Bookings</Th>
              <Th align="right">Total</Th>
              <Th align="right">Refunded</Th>
              <Th>Status</Th>
              <Th>Method</Th>
              <Th>Paid at</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><Td colSpan={8} style={{ color: 'hsl(var(--muted-foreground))', padding: '1rem 0.5rem' }}>
                Loading grouped payments…
              </Td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <Td colSpan={8} style={{ color: 'hsl(var(--muted-foreground))', padding: '1rem 0.5rem' }}>
                  No grouped payments matching this filter. Basket-style payments show up here after parents check out with multiple bookings.
                </Td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.intent_id} data-testid={`payment-intent-row-${r.intent_id}`}
                  style={{ borderTop: '1px solid hsl(var(--border))' }}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{r.parent_name || '—'}</div>
                  {r.parent_email && (
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{r.parent_email}</div>
                  )}
                </Td>
                <Td>{r.booking_count}</Td>
                <Td align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {symbol}{Number(r.total_amount || 0).toFixed(2)}
                </Td>
                <Td align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Number(r.refunded_amount || 0) > 0 ? (
                    <span style={{ color: 'hsl(0 60% 40%)', fontWeight: 600 }}>
                      −{symbol}{Number(r.refunded_amount).toFixed(2)}
                    </span>
                  ) : '—'}
                </Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td>{humanMethod(r.payment_method)}</Td>
                <Td>{formatDate(r.paid_at || r.created_at)}</Td>
                <Td align="right">
                  {(r.status === 'paid' || r.status === 'partially_refunded') && Number(r.refundable_amount || 0) > 0 ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setOpenIntentId(r.intent_id)}
                      data-testid={`payment-intent-refund-btn-${r.intent_id}`}
                      style={{
                        color: 'hsl(0 65% 45%)', borderColor: 'hsl(0 65% 45% / 0.4)',
                      }}
                    >↩ Refund</button>
                  ) : (
                    <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>—</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaymentIntentRefundDialog
        open={!!openIntentId}
        intentId={openIntentId}
        onClose={() => setOpenIntentId(null)}
        onRefunded={() => { setOpenIntentId(null); load(); }}
      />
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th style={{
      padding: '0.5rem', fontWeight: 600, fontSize: '0.75rem',
      letterSpacing: 0.3, textTransform: 'uppercase',
      textAlign: align || 'left',
      borderBottom: '1px solid hsl(var(--border))',
    }}>{children}</th>
  );
}

function Td({ children, colSpan, align, style }) {
  return (
    <td colSpan={colSpan}
        style={{ padding: '0.6rem 0.5rem', verticalAlign: 'top', textAlign: align || 'left', ...(style || {}) }}>
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { label: 'Paid', bg: 'hsl(140 55% 92%)', fg: 'hsl(140 55% 25%)' },
    partially_refunded: { label: 'Partial refund', bg: 'hsl(35 90% 92%)', fg: 'hsl(35 85% 30%)' },
    refunded: { label: 'Refunded', bg: 'hsl(0 65% 94%)', fg: 'hsl(0 65% 35%)' },
    pending_mollie: { label: 'Pending card', bg: 'hsl(220 60% 94%)', fg: 'hsl(220 55% 35%)' },
    pending_bank_transfer: { label: 'Pending transfer', bg: 'hsl(220 60% 94%)', fg: 'hsl(220 55% 35%)' },
    draft: { label: 'Draft', bg: 'hsl(var(--muted))', fg: 'hsl(var(--muted-foreground))' },
  };
  const conf = map[status] || { label: status || '—', bg: 'hsl(var(--muted))', fg: 'hsl(var(--muted-foreground))' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: '0.72rem', fontWeight: 600,
      background: conf.bg, color: conf.fg,
    }}>{conf.label}</span>
  );
}

function humanMethod(m) {
  switch (m) {
    case 'mollie':
    case 'mollie_intent':
      return 'Card';
    case 'bank_transfer':
      return 'Bank transfer';
    case 'manual':
      return 'Manual';
    default:
      return m || '—';
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_e) {
    return '—';
  }
}

export default PaymentIntentsSection;
